import React, { useEffect } from 'react';
import { FormikProvider, useFormik } from 'formik';
import * as Yup from 'yup';

import { useWorkflowContext } from 'app-context/worflow/WorkflowContext';
import RightSlider from 'component/hoc/rightSlider/RightSlider';
import CarouselForm from 'component/organism/workflow/forms/carousel/CarouselForm';
import toast from 'react-hot-toast';
import { isEqual } from 'lodash';

const formatCarouselData = (values) => ({
  carousel: {
    details: values.details.map((card) => ({
      cardTitle: card.cardTitle,
      cardDescription: card.cardDescription,
      blobUrl: card?.blobUrl ? card?.blobUrl : '',
      // fileName: card.uploadOrUrl === 'upload' ? card.fileName : 'asd',
      uploadOrUrl: card?.blobUrl ? 'url' : 'upload',
      file:
        card.uploadOrUrl === 'upload' && card.file
          ? {
              fileName: card.file.fileName,
              contentType: card.file.contentType,
              fileContent: card.file.fileContent,
            }
          : null,
    })),
  },
});

const singleCardSchema = Yup.object().shape({
  cardTitle: Yup.string().trim().required('Title is required.'),
  cardDescription: Yup.string().trim(),
  uploadOrUrl: Yup.string().required('Select "Upload" or "Add URL".'),
  file: Yup.object()
    .nullable()
    .when('uploadOrUrl', {
      is: 'upload',
      then: (schema) => schema.required('Image is Required'),
    }),
  url: Yup.string().when('uploadOrUrl', {
    is: 'url',
    then: (schema) =>
      schema
        .required('URL is required when "Add URL" is selected.')
        .url('Must be a valid URL.'),
  }),
});

const carouselValidationSchema = Yup.object().shape({
  details: Yup.array()
    .of(singleCardSchema)
    .min(1, 'At least one card is required.'),
});

const CarouselModal = ({ isOpen, onClose, title, id, isError }) => {
  const {
    workflowData,
    setWorkflowData,
    selectedNode,
    initialWorkflowData,
    setIsChanged,
  } = useWorkflowContext();

  const initialCards = selectedNode?.carousel?.details
    ? selectedNode.carousel.details.map((detail) => ({
        ...detail,
        uploadOrUrl: detail.blobUrl ? 'url' : 'upload',
      }))
    : [
        {
          uploadOrUrl: 'upload',
          fileName: '',
          blobUrl: '',
          cardTitle: '',
          cardDescription: '',
          file: null,
        },
      ];

  const formik = useFormik({
    initialValues: { details: initialCards },
    enableReinitialize: true,
    validationSchema: carouselValidationSchema,
    onSubmit: (values, { setSubmitting, resetForm }) => {},
  });
  const initialNode = initialWorkflowData?.find((node) => node?.id === id);
  const handleSubmit = async () => {
    await formik.handleSubmit();
    try {
      setTimeout(() => {
        const formattedData = formatCarouselData(formik.values);

        const updatedNodes = workflowData?.nodes?.map((node) =>
          node.id === id
            ? {
                ...node,
                carousel: formattedData.carousel,
                isError: isError,
              }
            : node
        );

        setWorkflowData((prev) => ({
          ...prev,
          nodes: updatedNodes,
        }));
        const initialNode = initialWorkflowData?.find(
          (node) => node?.id === id
        );
        const initialData = initialNode?.carousel?.details?.map((item) => ({
          blobUrl: item.blobUrl,
          cardDescription: item.cardDescription,
          cardTitle: item.cardTitle,
        }));

        const formikData = formik.values.details.map((item) => ({
          blobUrl: item.blobUrl,
          cardDescription: item.cardDescription,
          cardTitle: item.cardTitle,
        }));

        const changed = !isEqual(initialData, formikData);
        setIsChanged((prev) => {
          const alreadyExists = prev?.includes(id);
          if (changed && !alreadyExists) {
            return [...prev, id];
          }
          if (!changed && alreadyExists) {
            return prev?.filter((nodeId) => nodeId !== id);
          }

          return prev;
        });
        onClose();
        formik.resetForm();
      }, [100]);
    } catch {
      toast.error('Something went wrong');
    }
  };

  useEffect(() => {
    const updatedNodes = workflowData?.nodes?.map((node) =>
      node.id === id ? { ...node, is_Error: isError } : node
    );

    setWorkflowData((prev) => ({
      ...prev,
      nodes: updatedNodes,
    }));
  }, [isError]);

  useEffect(() => {
    if (isError) {
      formik.handleSubmit();
    }
  }, [isOpen]);

  return (
    <RightSlider
      title={title}
      isOpen={isOpen}
      action={{
        isOpen,
        setIsOpen: handleSubmit,
      }}
    >
      <FormikProvider value={formik}>
        <CarouselForm allowedExtensions={['jpg', 'jpeg', 'png']} />
      </FormikProvider>
    </RightSlider>
  );
};

export default CarouselModal;
